import { alpha, type ThemeOptions } from '@mui/material/styles';

import {
  STACKED_TABLE_BELOW_PX,
  controlHeight,
  touchTarget,
  darkColors,
  elevation,
  fontStacks,
  fontWeights,
  lightColors,
  radius,
  type ColorTokens,
} from './tokens';

/**
 * Theme Material UI du back-office, **derive** des jetons de `tokens.ts`.
 *
 * Le moteur reste React Admin + Material UI (ADR 0024) : ce theme habille MUI
 * au lieu de le remplacer. Deux consequences :
 *
 * - les ecrans existants, ecrits en composants MUI et React Admin bruts,
 *   prennent la nouvelle identite **sans etre modifies** (palette, rayons,
 *   boutons sans capitales, fonds chauds en sombre) ;
 * - le kit (`ui/kit/`) lit les 37 jetons semantiques par `palette.tokens` —
 *   `sx={{ bgcolor: 'tokens.surfaceMuted' }}` — plutot que par les couleurs
 *   generiques de MUI, qui n'en ont que six.
 *
 * Les couleurs generiques (`primary`, `error`…) sont choisies pour rester
 * lisibles **en encre comme en aplat**, parce que MUI emploie la meme valeur
 * pour un libelle de champ en erreur et pour le fond d'un bouton `contained`.
 * C'est pourquoi `error` reprend `criticalStrong` et non `danger` : `danger`
 * n'atteint que le seuil des objets graphiques (3:1), pas celui du texte.
 */

declare module '@mui/material/styles' {
  interface Palette {
    /** Les jetons semantiques du mobile, pour le mode courant. */
    tokens: ColorTokens;
  }

  interface PaletteOptions {
    tokens?: ColorTokens;
  }
}

type Mode = 'light' | 'dark';

/**
 * Contour d'un champ de saisie au repos : `textMuted` a 75 % d'opacite.
 *
 * `border` est un separateur (environ 1,3:1) et le restera, comme sur le
 * mobile. Mais un champ pose sur une carte a le fond de la carte : son contour
 * est la **seule** limite visible de la zone de saisie, et doit tenir 3:1
 * (WCAG 1.4.11). Derive de `textMuted` plutot que nouveau jeton : la parite
 * avec le mobile reste entiere. Rapport verifie par `tokens.contrast.test.ts`.
 */
// 0,7 tenait 3,24:1 contre l'interieur du champ mais 2,79:1 contre le fond de
// page clair, ou React Admin pose ses filtres de liste ; 0,75 tient 3,60 et 3,10.
export const INPUT_OUTLINE_OPACITY = 0.75;

export const inputOutline = (colors: ColorTokens): string => alpha(colors.textMuted, INPUT_OUTLINE_OPACITY);

/** Encre posee sur un aplat d'etat (succes, avertissement, info). */
const inkOnStatus = (colors: ColorTokens, mode: Mode): string =>
  // En clair les encres d'etat sont sombres : le blanc de la surface s'y lit.
  // En sombre elles sont claires : il leur faut le fond le plus profond.
  mode === 'light' ? colors.surface : colors.background;

const buildTheme = (mode: Mode): ThemeOptions => {
  const colors = mode === 'light' ? lightColors : darkColors;
  const shadows = elevation[mode];
  const onStatus = inkOnStatus(colors, mode);

  return {
    breakpoints: { values: { xs: 0, sm: STACKED_TABLE_BELOW_PX, md: 900, lg: 1200, xl: 1536 } },
    palette: {
      mode,
      tokens: colors,
      primary: { main: colors.primary, contrastText: colors.textOnPrimary },
      secondary: { main: colors.textStrong, contrastText: colors.surface },
      error: { main: colors.criticalStrong, contrastText: colors.textOnCritical },
      warning: { main: colors.warningText, contrastText: onStatus },
      success: { main: colors.successText, contrastText: onStatus },
      info: { main: colors.infoText, contrastText: onStatus },
      background: { default: colors.background, paper: colors.surface },
      text: { primary: colors.textStrong, secondary: colors.textMuted },
      divider: colors.border,
    },
    shape: { borderRadius: radius.table },
    typography: {
      fontFamily: fontStacks.brand,
      fontWeightRegular: fontWeights.regular,
      fontWeightMedium: fontWeights.semiBold,
      fontWeightBold: fontWeights.bold,
      h1: { fontWeight: fontWeights.extraBold },
      h2: { fontWeight: fontWeights.extraBold },
      h3: { fontWeight: fontWeights.extraBold },
      h4: { fontWeight: fontWeights.extraBold },
      h5: { fontWeight: fontWeights.bold },
      h6: { fontWeight: fontWeights.bold },
      subtitle2: { fontWeight: fontWeights.bold },
      overline: { fontWeight: fontWeights.bold, letterSpacing: '0.08em' },
      // Les majuscules forcees de MUI abiment les libelles francais accentues et
      // allongent les intitules metier.
      button: { fontWeight: fontWeights.bold, textTransform: 'none' },
    },
    components: {
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          // Le focus clavier doit se voir sur toute surface : un anneau plein en
          // couleur d'action, decale pour ne pas se confondre avec la bordure.
          root: {
            '&.Mui-focusVisible': { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // Sous 600 px, la cible tactile monte a 44 px, taille S comprise :
          // l'ecran se consulte aussi au telephone, d'astreinte (BO-14).
          root: ({ theme }) => ({
            borderRadius: radius.control,
            minHeight: controlHeight.M,
            paddingInline: 16,
            fontSize: 14,
            [theme.breakpoints.down('sm')]: { minHeight: touchTarget },
          }),
          sizeSmall: ({ theme }) => ({
            minHeight: controlHeight.S,
            paddingInline: 12,
            fontSize: 13,
            [theme.breakpoints.down('sm')]: { minHeight: touchTarget },
          }),
        },
        // React Admin pose ses boutons texte (« Créer », « Exporter ») sur le
        // fond de page, ou `primary` clair ne tient que 4,45:1 : meme encre que
        // le bouton fantome du kit. MUI 9 colore ces variantes par des
        // variables CSS, et non par une cle `textPrimary`.
        variants: [
          {
            props: { color: 'primary', variant: 'text' },
            style: { '--variant-textColor': colors.accentPinkStrong },
          },
          {
            props: { color: 'primary', variant: 'outlined' },
            style: { '--variant-outlinedColor': colors.accentPinkStrong },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: radius.control } },
      },
      MuiPaper: {
        // En sombre, MUI eclaircit les surfaces par un degrade blanc selon
        // l'elevation : il blanchirait les fonds chauds du mobile.
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.raised,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.dialog,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.overlay,
          },
          // Plein ecran sous 900 px : ni rayon ni cadre, la boite est l'ecran.
          paperFullScreen: { borderRadius: 0, border: 0 },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: colors.backdrop } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            backgroundColor: colors.surface,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: inputOutline(colors) },
            '&:hover:not(.Mui-disabled, .Mui-focused, .Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.textMuted,
            },
            '&.Mui-disabled': { backgroundColor: colors.surfaceMuted },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: colors.border },
          head: {
            fontSize: 12,
            fontWeight: fontWeights.bold,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            backgroundColor: colors.surfaceMuted,
          },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: fontWeights.semiBold, fontSize: 14 } },
      },
      MuiLink: {
        // Un lien peut se poser hors carte (« Revenir à la liste ») : meme
        // raison que les boutons texte.
        defaultProps: { color: colors.accentPinkStrong },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: fontWeights.semiBold } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: radius.table } },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
    },
  };
};

export const lightTheme: ThemeOptions = buildTheme('light');
export const darkTheme: ThemeOptions = buildTheme('dark');
